import { useMemo } from "react";
import { useAppDispatch, useAppSelector } from "@/stores/hook";
import { setIsStageBypassConfirmation } from "@/features/leads/stores/leadFunctionalSlice";
import { toast } from "sonner";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import useLeadPipeline from "@/features/leads/hook/useLeadPipeline";
import ConfirmStageMoveDialog from "./ConfirmStageMoveDialog";

/**
 * Confirmation modal for the "Update" action on the lead drawer's Stage
 * section. Reads the current draft (from `stageData`) plus the server's
 * current stage from the pipeline, and renders the shared
 * {@link ConfirmStageMoveDialog} with consequence copy that adapts to the
 * intent (skip / rollback / update-only / move-to-next).
 */
export default function StageBypassConfirmation({ itemId, stageData }: any) {
  const { isStageBypassConfirmation } = useAppSelector(
    (state) => state.leadFunctional
  );
  const dispatch = useAppDispatch();
  const { updateStage } = useActiveDataMutation();
  const { pipeline } = useLeadPipeline();

  const isValid =
    stageData &&
    stageData.stageId &&
    stageData.statusId &&
    stageData.subStatusId;

  const hasChanged =
    stageData &&
    (stageData.stageId !== stageData.serverStageId ||
      stageData.statusId !== stageData.serverStatusId ||
      stageData.subStatusId !== stageData.serverSubStatusId);

  const serverStageObj = useMemo(() => {
    if (!pipeline?.length || !stageData?.serverStageId) return null;
    return (
      pipeline.find(
        (s: any) => String(s.id) === String(stageData.serverStageId)
      ) ?? null
    );
  }, [pipeline, stageData?.serverStageId]);

  const mode = useMemo<
    "next" | "skip" | "rollback" | "update"
  >(() => {
    if (!stageData) return "update";
    if (stageData.stageId === stageData.serverStageId) return "update";
    const targetOrder = stageData.stage?.order;
    const sourceOrder = serverStageObj?.order;
    if (targetOrder == null || sourceOrder == null) return "next";
    if (Number(targetOrder) < Number(sourceOrder)) return "rollback";
    if (Number(targetOrder) - Number(sourceOrder) > 1) return "skip";
    return "next";
  }, [stageData, serverStageObj]);

  const handleClose = (next: boolean) => {
    dispatch(setIsStageBypassConfirmation(next));
  };

  const handleConfirm = () => {
    if (!isValid) {
      toast.error("Please select a valid stage, status and sub-status.");
      return;
    }

    if (!hasChanged) {
      toast.info("No changes detected.");
      handleClose(false);
      return;
    }

    updateStage.mutate(
      {
        id: itemId,
        data: {
          stageId: Number(stageData.stageId),
          statusId: Number(stageData.statusId),
          subStatusId: Number(stageData.subStatusId),
        },
      },
      {
        onSuccess: () => {
          // Drawer/board/timeline refresh lives in the updateStage hook; a
          // second invalidation here would fire duplicate GETs.
          toast.success("Stage updated successfully");
          handleClose(false);
        },
        onError: (error: any) => {
          const msg = error.response?.data?.message || "Failed to update lead.";
          toast.error(msg);
        },
      }
    );
  };

  return (
    <ConfirmStageMoveDialog
      open={isStageBypassConfirmation}
      onOpenChange={handleClose}
      currentStage={
        serverStageObj
          ? { id: serverStageObj.id, name: serverStageObj.name }
          : null
      }
      targetStage={
        stageData?.stage
          ? { id: stageData.stage.id, name: stageData.stage.name }
          : null
      }
      targetStatus={
        stageData?.status
          ? { id: stageData.status.id, name: stageData.status.name }
          : undefined
      }
      targetSubStatus={
        stageData?.subStatus
          ? { id: stageData.subStatus.id, name: stageData.subStatus.name }
          : undefined
      }
      mode={mode}
      isPending={updateStage.isPending}
      onConfirm={handleConfirm}
    />
  );
}
